import { useEffect, useRef, useState } from 'react'

import estilos from './CamaraEnVivo.module.css'

/**
 * LA FOTO SE TOMA AQUÍ, NO SE TRAE DE NINGUNA PARTE.
 *
 * Un `<input type="file" capture>` no garantiza nada: `capture` es una
 * SUGERENCIA al navegador. En escritorio se ignora y abre el explorador de
 * archivos; en muchos Android la cámara se abre pero con un botón para saltar a
 * la galería. Así que mientras haya un input de archivo, la foto puede venir de
 * cualquier sitio y de cualquier día.
 *
 * Esto abre la cámara dentro de la aplicación y captura el fotograma: no hay
 * explorador que abrir ni galería a la que saltar. Lo que sale de aquí se tomó
 * ahora mismo, y encima se le quema el sello con la fecha, la hora y las
 * coordenadas.
 *
 * Por qué importa: estas fotos sustentan una cotización y acaban en el acta que
 * el cliente firma. Si lo que prueba cómo estaba una instalación puede haberse
 * tomado en otra obra, no prueba nada.
 */

type Props = {
  abierto: boolean
  onCerrar: () => void
  /** Se llama con cada captura. Devuelve la imagen sin sellar. */
  onTomada: (foto: File) => void | Promise<void>
}

export function CamaraEnVivo({ abierto, onCerrar, onTomada }: Props) {
  const video = useRef<HTMLVideoElement>(null)
  const flujo = useRef<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [tomadas, setTomadas] = useState(0)

  useEffect(() => {
    if (!abierto) return
    let vivo = true

    async function encender() {
      setError(null)
      try {
        const media = await navigator.mediaDevices.getUserMedia({
          // La de atrás: es con la que se fotografía un tablero. `ideal` y no
          // `exact` para que en un portátil sin cámara trasera use la que hay
          // en vez de fallar.
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
          audio: false,
        })

        if (!vivo) {
          media.getTracks().forEach((t) => t.stop())
          return
        }

        flujo.current = media
        if (video.current) {
          video.current.srcObject = media
          await video.current.play().catch(() => {})
        }
      } catch (fallo) {
        const nombre = (fallo as { name?: string })?.name
        setError(
          nombre === 'NotAllowedError'
            ? 'No se dio permiso para la cámara. Hay que concederlo en los ajustes del navegador.'
            : nombre === 'NotFoundError'
              ? 'Este aparato no tiene cámara.'
              : 'No se pudo abrir la cámara.',
        )
      }
    }

    void encender()

    // SE APAGA AL CERRAR, siempre. Un flujo que queda abierto deja la luz de
    // la cámara encendida y consume batería en un teléfono que está en la
    // calle todo el día.
    return () => {
      vivo = false
      flujo.current?.getTracks().forEach((t) => t.stop())
      flujo.current = null
    }
  }, [abierto])

  async function disparar() {
    const v = video.current
    if (!v || !v.videoWidth || ocupado) return

    setOcupado(true)
    try {
      const lienzo = document.createElement('canvas')
      lienzo.width = v.videoWidth
      lienzo.height = v.videoHeight
      lienzo.getContext('2d')?.drawImage(v, 0, 0)

      const blob = await new Promise<Blob | null>((listo) =>
        lienzo.toBlob(listo, 'image/jpeg', 0.9),
      )
      if (!blob) return

      const foto = new File([blob], `visita-${Date.now()}.jpg`, { type: 'image/jpeg' })
      await onTomada(foto)
      setTomadas((n) => n + 1)
    } finally {
      setOcupado(false)
    }
  }

  if (!abierto) return null

  return (
    <div className={estilos.velo} role="dialog" aria-modal="true" aria-label="Tomar foto">
      <div className={estilos.marco}>
        {error ? (
          <div className={estilos.error}>
            <p>{error}</p>
          </div>
        ) : (
          <video ref={video} className={estilos.video} playsInline muted />
        )}

        <div className={estilos.barra}>
          <button type="button" className={estilos.cerrar} onClick={onCerrar}>
            {tomadas > 0 ? `Listo · ${tomadas}` : 'Cancelar'}
          </button>

          {!error && (
            <button
              type="button"
              className={estilos.disparador}
              onClick={() => void disparar()}
              disabled={ocupado}
              aria-label="Tomar la foto"
            />
          )}

          {/* Ocupa el hueco del botón de cerrar para que el disparador quede
              centrado de verdad y no a ojo. */}
          <span className={estilos.hueco} aria-hidden="true" />
        </div>
      </div>
    </div>
  )
}
